import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "../../../components/Alert/Alert";
import Loader from "../../../components/Loader";
import Spinner from "../../../components/Spinner";
import InputField from "../../../components/UIComponents/InputField";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../context/Auth/AuthContext";
import { getErrorMessage } from "../../../handlers/errorHandling";
import { calculateMaximumRedeemablePoints, currencyFormat, formatAmount } from "../../../handlers/utilityFunctions";
import { phoneTo255 } from "../../../handlers/valueTransformation";
import { ResumeDataService } from "../../../services/create-resume/resume-data";

export interface PaymentDetails {
	fullName: string;
	phoneNumber: string;
	amount: number;
	packageDetail: string;
	emailAddress: string;
	modeOfPayment?: string;
	originalAmount?: number;
	amountPaidByPoints?: number;
	redeemableRewardIds?: string[];
}

interface CheckoutProps {
	setCurrentStep: (step: number) => void;
	checkout: (paymentDetails: PaymentDetails) => void;
	setPaymentDetails: React.Dispatch<React.SetStateAction<PaymentDetails>>;
	paymentDetails: PaymentDetails;
	loading: boolean;
	serverErrors: string[];
	selectedPackage: string;
}

function Checkout({
	setCurrentStep,
	checkout,
	setPaymentDetails,
	paymentDetails,
	loading,
	serverErrors,
	selectedPackage,
}: CheckoutProps) {
	const { userData } = useAuth();
	const apiService = useRef(new ResumeDataService()).current;
	const [loadingRewards, setLoadingRewards] = useState(true);
	const [usePoints, setUsePoints] = useState(false);
	const [rewards, setRewards] = useState({
		amountPaidByPoints: 0,
		amount: 0,
		originalAmount: 0,
		rewards: [] as unknown[],
		totalAvailablePoints: 0,
		redeemableRewardIds: [] as string[],
	});

	const getPaymentsRewards = () => {
		apiService.getPaymentsRewards(
			(data: { data: Array<{ rewardStatus: string; rewardPoints: number }> }) => {
				const availablePoints = data.data.filter((item) => item.rewardStatus === "CLAIMED" && item.rewardPoints > 0);
				const payWithPointsDetails = calculateMaximumRedeemablePoints(selectedPackage, availablePoints);
				setRewards(payWithPointsDetails);
				setLoadingRewards(false);
			},
			(error: unknown) => {
				// Naming what failed matters here: the plan is still payable at full price, so this is
				// about the discount going missing rather than the checkout being broken.
				toast.error(getErrorMessage(error, "We could not load your reward points."));
				setLoadingRewards(false);
			},
		);
	};

	useEffect(() => {
		getPaymentsRewards();
	}, []);

	const onUsePoints = () => {
		if (!usePoints) {
			setPaymentDetails((prev) => ({
				...prev,
				modeOfPayment: "POINTS_AND_CASH",
				originalAmount: rewards.originalAmount,
				amount: rewards.amount,
				amountPaidByPoints: rewards.amountPaidByPoints,
				redeemableRewardIds: rewards.redeemableRewardIds,
			}));
		}
		if (usePoints) {
			const newPaymentDetails = { ...paymentDetails };
			delete newPaymentDetails.modeOfPayment;
			delete newPaymentDetails.originalAmount;
			delete newPaymentDetails.amountPaidByPoints;
			delete newPaymentDetails.redeemableRewardIds;
			setPaymentDetails({
				...newPaymentDetails,
				amount: rewards.originalAmount,
			});
		}
		setUsePoints(!usePoints);
	};

	function populatePaymentDetailsFromAccount() {
		if (paymentDetails.fullName === "" && paymentDetails.phoneNumber === "" && paymentDetails.emailAddress === "") {
			if (userData) {
				const { firstName, lastName, phoneNumber, email } = userData;
				setPaymentDetails((prev) => ({
					...prev,
					fullName: `${firstName} ${lastName}`,
					phoneNumber: phoneTo255(phoneNumber),
					emailAddress: email,
				}));
			}
		}
	}

	useEffect(() => {
		populatePaymentDetailsFromAccount();
	}, [userData]);

	function handleChange(e: ChangeEvent<HTMLInputElement>) {
		const { name, value } = e.target;
		setPaymentDetails({ ...paymentDetails, [name]: value });
	}

	const [errors, setErrors] = useState<string[]>([...serverErrors]);

	useEffect(() => {
		setErrors([...serverErrors]);
	}, [serverErrors]);

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const { fullName, phoneNumber } = paymentDetails;

		const errors: string[] = [];
		if (!fullName) {
			errors.push("Your name is required");
		}
		if (!phoneNumber) {
			errors.push("Phone number is required");
		}
		if (!paymentDetails.amount || !paymentDetails.packageDetail) {
			setCurrentStep(1);
		}
		if (errors.length > 0) {
			setErrors(errors);
			return;
		}

		checkout(paymentDetails);
	}

	return (
		<>
			{loadingRewards ? (
				<Loader />
			) : (
				<section className="bg-white py-6">
					<div className="mx-auto max-w-[1000px] px-4">
						<h1 className="text-center text-2xl font-semibold">Payment details</h1>
						<p className="mt-2 text-center text-gray-600">
							Enter your payment details below. You&#39;ll receive a message on the entered phone number with a payment
							link or a push to complete the payment.
						</p>
						<p className="mb-4 text-center text-gray-700">
							Amount due{" "}
							<span className="font-semibold text-gray-900">
								{"Tsh " + currencyFormat(paymentDetails.amount) + "/="}
							</span>
						</p>

						<form method="post" className="space-y-4">
							{errors.length > 0 && (
								<Alert variant="destructive">
									<AlertDescription>
										<ul className="list-inside list-disc space-y-1">
											{errors.map((error, index) => (
												<li key={index}>{error}</li>
											))}
										</ul>
									</AlertDescription>
								</Alert>
							)}

							{rewards.totalAvailablePoints > 0 && (
								<div
									className="cursor-pointer rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50"
									onClick={onUsePoints}
								>
									<div className="flex items-start gap-4">
										<input
											type="checkbox"
											checked={usePoints}
											onChange={onUsePoints}
											className="mt-0.5 h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-600"
										/>
										<div>
											<p className="font-medium">
												Use {rewards.amountPaidByPoints} out of {formatAmount(rewards.totalAvailablePoints)} reward
												points to unlock savings!
											</p>
											<div className="mt-1 flex flex-wrap items-center gap-2">
												<p className="text-sm text-gray-700">
													<span>Only Pay TZS </span>
													<span className="text-lg font-semibold">{formatAmount(rewards.amount)}</span>
													/=
												</p>
												<span className="rounded-full bg-green-600 px-3 py-0.5 text-sm text-white">
													You save TZS {formatAmount(rewards.amountPaidByPoints)}/=
												</span>
											</div>
										</div>
									</div>
								</div>
							)}

							<InputField
								type="text"
								name="phoneNumber"
								placeholder="Phone Number"
								value={paymentDetails.phoneNumber}
								onChange={handleChange}
								label="Phone Number"
								id="phoneNumber"
							/>
							<InputField
								type="text"
								name="fullName"
								placeholder="Full Name"
								value={paymentDetails.fullName}
								onChange={handleChange}
								label="Full Name"
								id="fullName"
							/>
							<InputField
								type="email"
								name="emailAddress"
								placeholder="Email Address"
								value={paymentDetails.emailAddress}
								onChange={handleChange}
								label="Email Address"
								id="emailAddress"
							/>

							<div className="flex justify-end gap-2 pt-2">
								<Button variant="outline" type="button" onClick={() => setCurrentStep(1)}>
									Back
								</Button>
								<Button type="submit" onClick={handleSubmit} disabled={loading}>
									{loading ? <Spinner /> : "Make Payment"}
								</Button>
							</div>
						</form>
					</div>
				</section>
			)}
		</>
	);
}

export default Checkout;
